import { SiteUrls } from '@tableau/shared-urls';
import { CheckBoxWidget, TextLinkWidget, Tooltip, TooltipPosition } from '@tableau/widgets';
import { BodyCell, DataGrid } from '@tableau/widgets-datagrid';
import React from 'react';
import { classes } from 'typestyle';

import { StyledCellFormatter } from '~src/app/spaces/ListView/StyledCellWrapper';
import { Messages } from '~src/codegen/Localize';
import { imgPath } from '~src/common/utilities/ImgUrl';
import { cssClass, textEllipsis } from '~src/style';
import { ContentLineageRelationshipInfo } from '~src/ts/models/ContentLineageRelationshipInfo';

const rootClass: string = 'ContentLineageRelationshipCell';

const lineHeight: string = '20px';

const ContentLineageRelationshipCellWrapper = cssClass(rootClass, 'Wrapper', {
  display: 'flex',
  flexDirection: 'row',
  paddingTop: '10px',
});

const CheckboxWrapper = cssClass(rootClass, 'CheckboxWrapper', {
  display: 'flex',
  marginLeft: '-12px',
  paddingRight: '12px'
});

const ContentItem = cssClass(rootClass, 'Item', textEllipsis, {
  height: lineHeight,
  lineHeight: lineHeight,
});

const ItemIcon = cssClass(rootClass, 'ItemIcon', {
  paddingRight: '6px',
  height: lineHeight,
  lineHeight: lineHeight,
});

const TextWrapper = cssClass(rootClass, 'TextWrapper', {
  display: 'flex',
  flexDirection: 'column',
});

const InnerTextWrapper = cssClass(rootClass, 'InnerTextWrapper', {
  display: 'flex',
  flexDirection: 'row',
});

const RelatedContentName = cssClass(rootClass, 'RelatedContentName', {
  fontWeight: 'bold',
  paddingLeft: '3px',
});


const InfoIcon = cssClass(rootClass, 'InfoIcon', {
  height: '14px',
  margin: '3px 0 0 6px',
});

const Disabled = cssClass(rootClass, 'Disabled', {
  opacity: 0.6,
});

const itemLinkStyle: React.CSSProperties = {
  height: lineHeight,
  lineHeight: lineHeight,
  marginTop: '-1px',
  paddingLeft: '3px'
};

const itemLinkStyleDisabled: React.CSSProperties = {
  ...itemLinkStyle,
  opacity: 0.6,
};

const flowIconImgPath = imgPath('spaces_small_flow.svg');
const infoIconImgPath = imgPath('info_14px_small.svg');

const handleCheckboxClick = (e: React.SyntheticEvent<HTMLInputElement, Event>) => {
  // let handleCellClick handle click events
  e.preventDefault();
};

const handleLinkClick = (e: React.SyntheticEvent<HTMLAnchorElement, Event>) => {
  // navigate to link but don't let handleCellClick get the event
  e.stopPropagation();
};

const getTooltipContent = () => <span>{Messages.linkedTaskPermissionsInfoTooltip()}</span>;

export function ContentLineageRelationshipCell(isSelected: (id: string) => boolean, onItemSelectionToggle: (row: DataGrid.Row) => void, siteUrls: SiteUrls): React.StatelessComponent<BodyCell.Props> {
  return StyledCellFormatter(
    function ContentLineageRelationshipCell(props: BodyCell.Props) {
      const lineageContentRelationship: ContentLineageRelationshipInfo= props.row.data as ContentLineageRelationshipInfo;
      const isDisabled: boolean = !lineageContentRelationship.userHasRunPermissions;

      const handleCellClick = (e: React.SyntheticEvent<HTMLDivElement, Event>) => {
        if (!isDisabled) {
          onItemSelectionToggle(props.row);
        }
        e.preventDefault();
        e.stopPropagation();
      };

      const relatedContentNames = lineageContentRelationship.relatedContent ? lineageContentRelationship.relatedContent.map((item) => item.relatedContentName) : [];
      const relatedContentText = relatedContentNames.join(', ');

      function renderRelatedContent() {
        return relatedContentText ? (
          <div className={classes(InnerTextWrapper, isDisabled ? Disabled : null)}>
            <div className={ContentItem}>
              {lineageContentRelationship.lineageRelationshipType === 'Downstream' ? Messages.linkedTasksDownstreamOf() : Messages.linkedTasksUpstreamOf()}
            </div>
            <div className={classes(ContentItem, RelatedContentName)} title={relatedContentText}>
              {relatedContentText}
            </div>
          </div>
        ) : null;
      }

      return lineageContentRelationship.content ? (
        /* eslint-disable jsx-a11y/no-static-element-interactions */
        /* eslint-disable jsx-a11y/click-events-have-key-events */
        <div
          onClick={handleCellClick}
          key={lineageContentRelationship.content.id}
          className={ContentLineageRelationshipCellWrapper}
          data-tb-test-id={'ContentLineageRelationshipCellTestId'}
        >
          <div className={CheckboxWrapper}>
            <CheckBoxWidget
              checked={isSelected(lineageContentRelationship.content.id)}
              handleChange={() => onItemSelectionToggle(props.row)}
              testId={'ContentLineageRelationshipCellCheckboxTestId'}
              removeLeftMargin={true}
              disabled={isDisabled}
              onClick={handleCheckboxClick}
            />
          </div>
          <img src={flowIconImgPath} className={classes(ItemIcon, isDisabled ? Disabled : null)} alt={Messages.flow()} />
          <div className={TextWrapper}>
            <div className={InnerTextWrapper}>
              <div className={classes(ContentItem, isDisabled ? Disabled : null)} title={lineageContentRelationship.content.name}>{Messages.linkedTasksRunFlowSentenceCasing()}</div>
              <TextLinkWidget
                href={siteUrls.flowOverviewUrl(lineageContentRelationship.content.id)}
                target={'_blank'}
                testId={'ContentLineageRelationshipCellLinkTestId'}
                title={lineageContentRelationship.content.name}
                style={isDisabled ? itemLinkStyleDisabled : itemLinkStyle}
                onClick={handleLinkClick}
              >
                {lineageContentRelationship.content.name}
              </TextLinkWidget>
              {isDisabled &&
              <Tooltip
                position={TooltipPosition.Right}
                getContent={getTooltipContent}
                maxWidth={220}
              >
                <img className={InfoIcon} src={infoIconImgPath} alt={''}/>
              </Tooltip>}
            </div>
            {renderRelatedContent()}
          </div>
        </div>
      ) : null;
    }
  );
}
